import * as React from 'react'

import {
  AGGREGATION_STUDENT,
  NEEDS_MORE_PRACTICE,
  ResultRow,
  formatLastPracticed,
  formatMomentumScore,
  getRowMetrics,
  getSmartGroup,
  getSmartGroupTooltipText,
  getSortableSmartGroup,
  smartGroupToClassName,
} from './helpers'

import { EXTRA_SMALL_ICON_BASE_SRC, Tooltip, pluralize, titleCaseMultiWordString, } from '../../Shared/index'

const helpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`

export const bucketRowsBySmartGroup = (rows: ResultRow[], goal: number) => (
  rows.reduce((acc, row) => {
    const { exposure, momentumScore, questionsAnswered, } = getRowMetrics(row)
    if (questionsAnswered === 0) {
      acc.unpracticed.push(row)
      return acc
    }
    const key = getSmartGroup(exposure, momentumScore, questionsAnswered, goal)
    if (!acc.grouped[key]) acc.grouped[key] = []
    acc.grouped[key].push(row)
    acc.practicedCount += 1
    return acc
  }, { grouped: {} as Record<string, ResultRow[]>, unpracticed: [] as ResultRow[], practicedCount: 0 })
)

export const cardMetricPrefix = (isIndividual: boolean) => (isIndividual ? '' : 'Avg. ')
export const cardFooterLine = (isIndividual: boolean, row: ResultRow) => (
  isIndividual
    ? `Last practiced on ${formatLastPracticed(row.last_practiced)}`
    : `${row.number_of_students ?? 0} ${pluralize(row.number_of_students ?? 0, 'student', 'students')} practiced`
)

interface CardMetricsProps {
  smartGroup: string;
  metricPrefix: string;
  momentumScore: number | null;
  exposure: string;
  questionsAnswered: number;
  goal: number;
}

export const CardMetrics: React.FC<CardMetricsProps> = ({ smartGroup, metricPrefix, momentumScore, exposure, questionsAnswered, goal, }) => {
  // Cards in the "Needs more practice" bucket have low exposure, so we colour the
  // momentum score using its underlying score rather than the bucket colour to
  // avoid implying the score itself is unreliable.
  const scoreClassName = smartGroup === NEEDS_MORE_PRACTICE
    ? smartGroupToClassName[getSmartGroup(exposure, momentumScore, questionsAnswered, goal, true)]
    : smartGroupToClassName[smartGroup]

  return (
    <div className="smart-group-metrics">
      <div className="metric">
        <h4 className="metric-label">{metricPrefix}Momentum Score</h4>
        <span className={`metric-value score ${scoreClassName}`}>
          {formatMomentumScore(momentumScore, exposure)}
        </span>
      </div>
      <div className="metric">
        <h4 className="metric-label">{metricPrefix}Exposure</h4>
        <span className={`metric-value exposure ${(exposure ?? '').toLowerCase()}`}>
          {exposure}
        </span>
      </div>
    </div>
  )
}

export interface ItemCardProps {
  row: ResultRow;
  smartGroup: string;
  aggregation: string;
  goal: number;
  isSpecificStudent?: boolean;
}

export const ItemCard: React.FC<ItemCardProps> = ({ row, smartGroup, aggregation, goal, isSpecificStudent = false, }) => {
  const { name, } = row
  const { momentumScore, exposure, questionsAnswered, } = getRowMetrics(row)

  const isIndividual = aggregation === AGGREGATION_STUDENT || isSpecificStudent
  const metricPrefix = cardMetricPrefix(isIndividual)
  const footerLine = cardFooterLine(isIndividual, row)

  return (
    <section className="smart-group-card">
      <div className="name-and-last-practiced">
        <h3>{name}</h3>
        <p className="skill-practice-info">{footerLine}</p>
      </div>
      <CardMetrics
        exposure={exposure}
        goal={goal}
        metricPrefix={metricPrefix}
        momentumScore={momentumScore}
        questionsAnswered={questionsAnswered}
        smartGroup={smartGroup}
      />
    </section>
  )
}

export const UnpracticedBanner = ({ heading, buttonText, onClick }: {
  heading: React.ReactNode,
  buttonText: string,
  onClick: () => void,
}) => (
  <section className="unpracticed-section-wrapper">
    <div className="unpracticed-section">
      <h2>{heading}</h2>
      <button
        className="quill-button outlined medium grey focus-on-light"
        onClick={onClick}
        type="button"
      >
        {buttonText}
      </button>
    </div>
  </section>
)

interface SmartGroupSectionProps {
  smartGroup: string;
  rows: ResultRow[];
  aggregation: string;
  goal: number;
  entityLabel: { singular: string; plural: string };
  getSortableEntityName: (aggregation: string, name: string) => string;
  // Number of cards shown before the "Show all" button; undefined renders all
  // cards upfront (small, fixed sets like skills and grades).
  visibleCount?: number;
  isSpecificStudent?: boolean;
  renderCard?: (row: ResultRow, smartGroup: string) => React.ReactNode;
}

export const SmartGroupSection: React.FC<SmartGroupSectionProps> = ({ smartGroup, rows, aggregation, goal, entityLabel, getSortableEntityName, visibleCount, isSpecificStudent = false, renderCard, }) => {
  const truncate = visibleCount != null
  const [showAll, setShowAll] = React.useState(!truncate)

  function toggleShowAll() { setShowAll(!showAll) }

  const sortedRows = React.useMemo(() => (
    rows
      .map((row) => ({
        row,
        sortKey: getSortableSmartGroup(smartGroup, getRowMetrics(row).momentumScore, getSortableEntityName(aggregation, row.name)),
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(({ row }) => row)
  ), [rows, smartGroup, aggregation])

  const tooltipText = getSmartGroupTooltipText(smartGroup, goal, titleCaseMultiWordString(entityLabel.plural))

  const visibleRows = showAll ? sortedRows : sortedRows.slice(0, visibleCount)

  return (
    <section className={`smart-group-section group-by-${aggregation} ${showAll ? 'show-all' : ''}`}>
      <div className="smart-group-header">
        <div className="smart-group-title">
          <h2>{titleCaseMultiWordString(smartGroup)}</h2>
          <Tooltip
            tooltipText={tooltipText}
            tooltipTriggerText={<img alt="Help icon" src={helpIconSrc} />}
          />
        </div>
        <div className="smart-group-stats">
          <span>{rows.length} {pluralize(rows.length, entityLabel.singular, entityLabel.plural)}</span>
        </div>
      </div>
      <div className="smart-group-cards-grid">
        {visibleRows.map((row) => (
          renderCard
            ? <React.Fragment key={row.aggregate_id}>{renderCard(row, smartGroup)}</React.Fragment>
            : (
              <ItemCard
                aggregation={aggregation}
                goal={goal}
                isSpecificStudent={isSpecificStudent}
                key={row.aggregate_id}
                row={row}
                smartGroup={smartGroup}
              />
            )
        ))}
      </div>
      {truncate && sortedRows.length > visibleCount && (
        <button
          className="quill-button focus-on-light small outlined green"
          onClick={toggleShowAll}
          type="button"
        >
          {showAll ? `Show fewer ${entityLabel.plural}` : `Show all ${sortedRows.length} ${entityLabel.plural}`}
        </button>
      )}
    </section>
  )
}
